import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import React from "react";
import { usePostNavigation } from "../hooks/usePostNavigation";
import LazyImage from "./LazyImage";

type ExplorerPostProps = {
  id: string;
  hashCode?: string; // Add hash code support
  username: string;
  userImage: string;
  img: string;
  caption: string;
  userId: string;
  video: string | null;
  postType?: string;
  tileSize?: 'tall' | 'wide' | 'square' | 'large' | 'medium';
  height?: number; // Random height for Pinterest-style masonry
};

const ExplorerPost: React.FC<ExplorerPostProps> = ({
  id,
  hashCode,
  username,
  img,
  caption,
  video,
  postType = 'regular',
  tileSize = 'square',
  height
}) => {
  const { navigateToPostById } = usePostNavigation();
  const isTextPost = postType === 'text' || !img;

  const handleClick = () => {
    if (hashCode) {
      navigateToPostById(id, hashCode);
    }
  };

  // Determine aspect ratio class based on tileSize
  // For Pinterest-style masonry, use provided height
  const getAspectClass = () => {
    if (height) {
      return ''; // Height will be set via inline style
    }
    switch (tileSize) {
      case 'tall':
        return 'h-full'; // Height handled by row-span-2
      case 'wide':
        return 'aspect-[2/1]'; // 2:1 ratio (wider, spans 2 columns)
      case 'large':
        return 'h-full'; // Height handled by row-span-2
      case 'square':
      default:
        return 'aspect-square'; // 1:1 ratio
    }
  };

  return (
    <motion.div
      className={`relative cursor-pointer overflow-hidden bg-gray-800 ${getAspectClass()} w-full mb-1 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] break-inside-avoid`}
      style={height ? { height: `${height}px` } : undefined}
      onClick={handleClick}
      whileHover={{ 
        scale: 1.02,
        transition: { duration: 0.2 }
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Text post: gradient background + caption */}
      {isTextPost ? (
        <div className="relative w-full h-full min-h-[120px] bg-gradient-to-br from-slate-700 via-gray-800 to-slate-900 flex flex-col items-center justify-center p-4">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-gray-700/30 via-transparent to-transparent" />
          {caption ? (
            <p className="text-sm text-gray-200 text-center line-clamp-4 leading-relaxed relative z-10">
              {caption}
            </p>
          ) : (
            <p className="text-xs text-gray-500 text-center relative z-10">متن پست</p>
          )}
        </div>
      ) : (
        /* Image container */
        <div className="relative w-full h-full">
          <LazyImage
            src={img}
            alt={caption}
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center top' }}
          />
          
          {/* Gradient overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300" />
        </div>
      )}
      
      {!isTextPost && video ? (
        <div className="absolute top-3 right-3">
          <div className="w-8 h-8 bg-black/70 rounded-full flex items-center justify-center backdrop-blur-sm">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </div>
        </div>
      ) : null}
      
      <div className="hidden absolute top-3 left-3">
        <div className="bg-black/70 backdrop-blur-sm rounded-full px-3 py-1">
          <p className="text-white text-xs font-medium">@{username}</p>
        </div>
      </div>
      
      {/* Caption overlay on hover (image posts only; text posts show caption in tile) */}
      {!isTextPost && (
        <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 hover:opacity-100 transition-opacity duration-300">
          <div className="text-white">
            <p className="text-sm font-medium mb-1">@{username}</p>
            <p className="text-xs line-clamp-2 leading-relaxed">{caption}</p>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default React.memo(ExplorerPost); 